import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Loader2, Calendar, FileText, X, Phone, Mail, Paperclip, Upload } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { atencionClienteAPI } from '../../api';
import { toast } from '../../hooks/use-toast';
import ConsultaDetailDialog from './ConsultaDetailDialog';
import { PRIORIDADES, ORIGENES, ESTADOS_CONSULTA } from './constants';
import { formatDate, formatDateTime } from './utils';

const AtencionClienteTab = ({ usuarios, config }) => {
  const [consultas, setConsultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterOrigen, setFilterOrigen] = useState('');
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingConsulta, setEditingConsulta] = useState(null);
  const [selectedConsulta, setSelectedConsulta] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [plantillas, setPlantillas] = useState([]);
  const [expedientesSearch, setExpedientesSearch] = useState([]);
  const [searchingExpediente, setSearchingExpediente] = useState(false);
  
  const [form, setForm] = useState({
    origen: 'telefono',
    cliente_nombre: '',
    cliente_apellidos: '',
    cliente_email: '',
    cliente_telefono: '',
    expediente_id: null,
    expediente_numero: '',
    asunto: '',
    descripcion_consulta: '',
    prioridad: 'media',
    asignado_a_id: '',
    asignado_a_nombre: ''
  });
  const [adjuntosNuevaConsulta, setAdjuntosNuevaConsulta] = useState([]);
  const [uploadingAdjunto, setUploadingAdjunto] = useState(false);

  useEffect(() => {
    loadConsultas();
    loadPlantillas();
  }, []);

  useEffect(() => {
    loadConsultas();
  }, [filterEstado, filterOrigen]);

  const loadConsultas = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterEstado) params.estado = filterEstado;
      if (filterOrigen) params.origen = filterOrigen;
      if (search) params.search = search;
      
      const res = await atencionClienteAPI.getConsultas(params);
      setConsultas(res.data.consultas);
    } catch (error) {
      console.error('Error cargando consultas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlantillas = async () => {
    try {
      const res = await atencionClienteAPI.getPlantillas(true);
      setPlantillas(res.data);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
    }
  };

  const resetForm = () => {
    setForm({
      origen: 'telefono',
      cliente_nombre: '',
      cliente_apellidos: '',
      cliente_email: '',
      cliente_telefono: '',
      expediente_id: null,
      expediente_numero: '',
      asunto: '',
      descripcion_consulta: '',
      prioridad: 'media',
      asignado_a_id: '',
      asignado_a_nombre: ''
    });
    setEditingConsulta(null);
    setExpedientesSearch([]);
    setAdjuntosNuevaConsulta([]);
  };

  const handleOpenNew = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleEdit = (consulta) => {
    setForm({
      origen: consulta.origen,
      cliente_nombre: consulta.cliente_nombre || '',
      cliente_apellidos: consulta.cliente_apellidos || '',
      cliente_email: consulta.cliente_email || '',
      cliente_telefono: consulta.cliente_telefono || '',
      expediente_id: consulta.expediente_id,
      expediente_numero: consulta.expediente_numero || '',
      asunto: consulta.asunto || '',
      descripcion_consulta: consulta.descripcion_consulta || '',
      prioridad: consulta.prioridad || 'media',
      asignado_a_id: consulta.asignado_a_id || '',
      asignado_a_nombre: consulta.asignado_a_nombre || ''
    });
    setEditingConsulta(consulta);
    setIsDialogOpen(true);
  };

  const handleDelete = async (consulta) => {
    if (!window.confirm('¿Está seguro de eliminar esta consulta?')) return;
    try {
      await atencionClienteAPI.deleteConsulta(consulta.id);
      toast({ title: 'Consulta eliminada' });
      loadConsultas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar', variant: 'destructive' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.cliente_nombre || !form.asunto || !form.descripcion_consulta) {
      toast({ title: 'Error', description: 'Complete los campos obligatorios', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        asignado_a_id: form.asignado_a_id ? parseInt(form.asignado_a_id) : null
      };
      
      let consultaId;
      if (editingConsulta) {
        await atencionClienteAPI.updateConsulta(editingConsulta.id, data);
        consultaId = editingConsulta.id;
        toast({ title: 'Consulta actualizada' });
      } else {
        const res = await atencionClienteAPI.createConsulta(data);
        consultaId = res.data.id;
        toast({ title: 'Consulta creada' });
      }
      
      // Subir adjuntos si hay
      if (adjuntosNuevaConsulta.length > 0 && consultaId) {
        for (const file of adjuntosNuevaConsulta) {
          const formData = new FormData();
          formData.append('file', file);
          try {
            await atencionClienteAPI.subirAdjuntoConsulta(consultaId, formData);
          } catch (err) {
            console.error('Error subiendo adjunto:', err);
          }
        }
        toast({ title: `${adjuntosNuevaConsulta.length} archivo(s) adjuntado(s)` });
      }
      
      setIsDialogOpen(false);
      resetForm();
      loadConsultas();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDetail = async (consulta) => {
    try {
      const res = await atencionClienteAPI.getConsulta(consulta.id);
      setSelectedConsulta(res.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast({ title: 'Error al cargar detalles', variant: 'destructive' });
    }
  };

  const searchExpedientes = async (query) => {
    if (query.length < 2) {
      setExpedientesSearch([]);
      return;
    }
    setSearchingExpediente(true);
    try {
      const res = await atencionClienteAPI.buscarExpedientes(query);
      setExpedientesSearch(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setSearchingExpediente(false);
    }
  };

  const Icon = config.icon;

  return (
    <>
      {/* Header */}
      <Card className={`${config.lightBg} border-l-4 ${config.borderColor}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 ${config.bgColor} rounded-full flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{config.nombre}</h2>
                <p className="text-sm text-gray-600">Peticiones de información de clientes por teléfono, email o WhatsApp</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por cliente, asunto..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadConsultas()}
                />
              </div>
            </div>
            
            <Select value={filterEstado || 'all'} onValueChange={(v) => setFilterEstado(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ESTADOS_CONSULTA).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterOrigen || 'all'} onValueChange={(v) => setFilterOrigen(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Origen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(ORIGENES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button className={`${config.bgColor} hover:opacity-90 text-white`} onClick={handleOpenNew}>
              <Plus className="w-4 h-4 mr-2" />
              NUEVA CONSULTA
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de consultas */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : consultas.length > 0 ? (
            <div className="divide-y">
              {consultas.map((consulta) => {
                const origenConfig = ORIGENES[consulta.origen] || ORIGENES.telefono;
                const estadoConfig = ESTADOS_CONSULTA[consulta.estado] || ESTADOS_CONSULTA.en_proceso;
                const OrigenIcon = origenConfig.icon;
                
                return (
                  <div 
                    key={consulta.id}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleOpenDetail(consulta)}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${origenConfig.color}`}>
                        <OrigenIcon className="w-5 h-5" />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-gray-900">
                              {consulta.cliente_nombre} {consulta.cliente_apellidos}
                            </p>
                            <p className="text-sm text-gray-600 truncate">{consulta.asunto}</p>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button 
                              className="p-1 hover:bg-gray-200 rounded"
                              onClick={() => handleEdit(consulta)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4 text-gray-600" />
                            </button>
                            <button 
                              className="p-1 hover:bg-gray-200 rounded"
                              onClick={() => handleDelete(consulta)}
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                          <Badge className={estadoConfig.color}>
                            {estadoConfig.nombre}
                          </Badge>
                          <Badge className={PRIORIDADES[consulta.prioridad]?.color || ''}>
                            {PRIORIDADES[consulta.prioridad]?.nombre || consulta.prioridad}
                          </Badge>
                          
                          {consulta.expediente_numero && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <FileText className="w-3 h-3" />
                              {consulta.expediente_numero}
                            </span>
                          )}
                          
                          {consulta.cliente_telefono && (
                            <span className="flex items-center gap-1 text-gray-600">
                              <Phone className="w-3 h-3" />
                              {consulta.cliente_telefono}
                            </span>
                          )}
                          
                          {consulta.cliente_email && (
                            <span className="flex items-center gap-1 text-gray-600">
                              <Mail className="w-3 h-3" />
                              {consulta.cliente_email}
                            </span>
                          )}
                          
                          <span className="flex items-center gap-1 text-gray-500">
                            <Calendar className="w-3 h-3" />
                            {formatDateTime(consulta.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No hay consultas de atención cliente</p>
              <Button variant="outline" className="mt-4" onClick={handleOpenNew}>
                <Plus className="w-4 h-4 mr-2" />
                Crear primera consulta
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nueva/Editar Consulta */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5" />
              {editingConsulta ? 'Editar Consulta' : 'Nueva Consulta de Cliente'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Origen */}
            <div>
              <Label>Origen de la consulta *</Label>
              <div className="flex gap-2 mt-2">
                {Object.entries(ORIGENES).map(([key, val]) => {
                  const OrigenIcon = val.icon;
                  return (
                    <Button
                      key={key}
                      type="button"
                      variant={form.origen === key ? 'default' : 'outline'}
                      className={form.origen === key ? config.bgColor : ''}
                      onClick={() => setForm({...form, origen: key})}
                    >
                      <OrigenIcon className="w-4 h-4 mr-2" />
                      {val.nombre}
                    </Button>
                  );
                })}
              </div>
            </div>

            {/* Datos del cliente */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={form.cliente_nombre}
                  onChange={(e) => setForm({...form, cliente_nombre: e.target.value})}
                  placeholder="Nombre del cliente"
                  required
                />
              </div>
              <div>
                <Label>Apellidos</Label>
                <Input
                  value={form.cliente_apellidos}
                  onChange={(e) => setForm({...form, cliente_apellidos: e.target.value})}
                  placeholder="Apellidos"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={form.cliente_telefono}
                  onChange={(e) => setForm({...form, cliente_telefono: e.target.value})}
                  placeholder="600 123 456"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.cliente_email}
                  onChange={(e) => setForm({...form, cliente_email: e.target.value})}
                  placeholder="cliente@email.com"
                />
              </div>
            </div>

            {/* Expediente relacionado */}
            <div>
              <Label>Expediente relacionado (opcional)</Label>
              <div className="relative">
                <Input
                  value={form.expediente_numero}
                  onChange={(e) => {
                    setForm({...form, expediente_numero: e.target.value, expediente_id: null});
                    searchExpedientes(e.target.value);
                  }}
                  placeholder="Buscar expediente por número o cliente..."
                />
                {searchingExpediente && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                )}
              </div>
              {expedientesSearch.length > 0 && (
                <div className="mt-1 border rounded-md shadow-sm max-h-40 overflow-y-auto">
                  {expedientesSearch.map((exp) => (
                    <div
                      key={exp.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setForm({
                          ...form,
                          expediente_id: exp.id,
                          expediente_numero: exp.numero_expediente
                        });
                        setExpedientesSearch([]);
                      }}
                    >
                      <p className="font-medium">{exp.numero_expediente}</p>
                      <p className="text-sm text-gray-600">{exp.cliente_nombre}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Consulta */}
            <div>
              <Label>Asunto *</Label>
              <Input
                value={form.asunto}
                onChange={(e) => setForm({...form, asunto: e.target.value})}
                placeholder="Resumen breve de la consulta"
                required
              />
            </div>

            <div>
              <Label>Descripción de la consulta *</Label>
              <Textarea
                value={form.descripcion_consulta}
                onChange={(e) => setForm({...form, descripcion_consulta: e.target.value})}
                placeholder="Detalle completo de la consulta del cliente..."
                rows={4}
                required
              />
            </div>

            {/* Prioridad y Asignación */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prioridad</Label>
                <Select value={form.prioridad} onValueChange={(v) => setForm({...form, prioridad: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORIDADES).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Asignar a</Label>
                <Select 
                  value={form.asignado_a_id?.toString() || 'none'} 
                  onValueChange={(v) => {
                    const user = usuarios.find(u => u.id.toString() === v);
                    setForm({
                      ...form, 
                      asignado_a_id: v && v !== 'none' ? parseInt(v) : null,
                      asignado_a_nombre: user?.nombre || ''
                    });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Adjuntos */}
            <div>
              <Label className="flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Archivos adjuntos (opcional)
              </Label>
              <div className="mt-2 space-y-2">
                {adjuntosNuevaConsulta.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {adjuntosNuevaConsulta.map((file, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-gray-100 rounded px-2 py-1">
                        <Paperclip className="w-3 h-3 text-gray-500" />
                        <span className="text-sm">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => setAdjuntosNuevaConsulta(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="cursor-pointer inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800">
                  <Upload className="w-4 h-4" />
                  Seleccionar archivos
                  <input 
                    type="file" 
                    multiple
                    className="hidden" 
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setAdjuntosNuevaConsulta(prev => [...prev, ...files]);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className={`${config.bgColor} hover:opacity-90 text-white`} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editingConsulta ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle Consulta */}
      <ConsultaDetailDialog
        consulta={selectedConsulta}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedConsulta(null);
        }}
        plantillas={plantillas}
        onUpdate={() => {
          loadConsultas();
        }}
        config={config}
      />
    </>
  );
};


export default AtencionClienteTab;
